import React from "react";
import { motion } from "framer-motion";
import { Check, X, Sparkles, CheckCircle, XCircle } from "lucide-react";

import { getProductDetailButtonName, productDetailStatusIcons, productDetailStautsColor } from "./common";
import { BASIC_TIER, ENTERPRISE_TIER, PRO_TIER } from "./types";

const ProductPlanCard = ({ plan, hideAction, onButtonClick, tierBought, product }: any) => {
  const icon = productDetailStatusIcons(plan.name);
  const isUpdagradeAble = (planName: string) => planName !== ENTERPRISE_TIER;

  const mapStatus = (statusName: string, feature: any) => {
    if (statusName === BASIC_TIER) return feature.isBasic;
    if (statusName === PRO_TIER) return feature.isPro;
    if (statusName === ENTERPRISE_TIER) return feature.isEnterprise;
    return false;
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      className={`
          relative ${productDetailStautsColor(plan.name, true)} 
          backdrop-blur-lg rounded-xl p-6 flex flex-col justify-between
          border border-white/10
          shadow-[0_0_15px_rgba(0,0,0,0.2)]
          hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]
          transition-shadow duration-500
        `}
    >
      <div>
        <div className="flex justify-between items-center mb-4">
          <motion.h3
            className="text-xl font-bold bg-clip-text text-gray-800"
            whileHover={{ scale: 1.05 }}
          >
            {plan.name}
          </motion.h3>
          <motion.div
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.5 }}
            className={` rounded-full text-center ${productDetailStautsColor(plan.name)}`}
          >
            <div className="p-3 w-12 h-12 text-white">
              {icon}
            </div>
          </motion.div>
        </div>

        <motion.p
          className="text-3xl font-bold mb-4 flex items-center gap-2"
          whileHover={{ scale: 1.05 }}
        >
          {plan.name !== ENTERPRISE_TIER ? (<>
            {`€ ${plan.price}`}
              <span className="text-sm font-normal text-gray-500">/month</span>
              <Sparkles className="w-5 h-5 text-yellow-500" />
            </>
          ) : <span className="text-[19px]">Contact for pricing</span>}
        </motion.p>

        <ul className="space-y-3 mb-6">
          {plan.tierFeatures[plan.name].map((feature: any, index: any) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center `}
            >
              <motion.div whileHover={{ scale: 1.2 }} className="mr-2">
                <Check className="w-5 h-5 text-green-500" />
              </motion.div>
              <span className="text-gray-600 ">{feature}</span>
            </motion.li>
          ))}
        </ul>
      </div>

      {!hideAction && (tierBought !== plan.name) && (
        (tierBought && plan.name !== BASIC_TIER && isUpdagradeAble(tierBought)) ? (
          // Mostra il pulsante di "Upgrade" solo se il piano corrente non è BASIC_TIER
          <motion.button
            whileHover={{ scale: 1.07 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              onButtonClick(plan.name);
            }}
            className={`
      w-full ${productDetailStautsColor(plan.name)} 
      cursor-pointer
      text-white py-3 px-4 rounded-lg
      flex items-center justify-center gap-2
      shadow-lg shadow-blue-500/30
      hover:shadow-xl hover:shadow-blue-500/40
      transition duration-300
    `}
          >
            Upgrade
            {productDetailStatusIcons(plan.name)}
          </motion.button>
        ) : (
          // Mostra il pulsante "Buy Now" per i piani non acquistati
          !tierBought && (
            <motion.button
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                onButtonClick(plan.name);
              }}
              className={`
        w-full ${productDetailStautsColor(plan.name)} 
        cursor-pointer
        text-white py-3 px-4 rounded-lg
        flex items-center justify-center gap-2
        shadow-lg shadow-blue-500/30
        hover:shadow-xl hover:shadow-blue-500/40
        transition duration-300
      `}
            >
              {plan.cta || getProductDetailButtonName(plan.name)}
              {productDetailStatusIcons(plan.name)}
            </motion.button>
          )
        )
      )}

      {!hideAction && tierBought && tierBought === plan.name && (
        <motion.p className="text-center bg-sky-500 rounded-lg p-2 flex items-center justify-center gap-2 py-3">
          <CheckCircle className="w-5 h-5 text-white" />
          <span className="text-white">Already Purchased</span>
        </motion.p>
      )}
      {!hideAction && tierBought && tierBought !== BASIC_TIER && plan.name === BASIC_TIER && (
        <motion.p className="text-center bg-red-500 rounded-lg p-2 flex items-center justify-center gap-2 py-3">
          <XCircle className="w-5 h-5 text-white" />
          <span className="text-white">Not Upgradable</span>
        </motion.p>
      )}
    </motion.div>
  );
};

export default ProductPlanCard;
